import React, { ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { DialogTitle } from '@headlessui/react';

import LoginLayout from '../../components/LoginLayout';
import Input from '../../widgets/Input';
import Button from '../../widgets/Button';
import { resetPassword } from '../../../shared/service/auth';
import PasswordStrengthValidator from './PasswordStrengthValidator';
import { IResetPassword } from '../../../shared/types/ResetPassword';
import {
  MAX_RETRY_CODE,
  ResetPasswordErrors,
} from '../../../shared/constants/ResetPassword';
import Modal from '../../widgets/Modal';

import reportStatus from '../../../assets/icons/report-status.png';

import { validateCode } from '../../../utilities/common/validateCode';
import { EventActions, EventNames } from '../../../shared/constants/analytics';
import useTracking from '../../../utilities/hooks/useTracking';

function ResetPasswordScreen({ state, setState }: IResetPassword) {
  const { email } = state;
  const [code, setCode] = useState<string>('');
  const [newPassword, setnewPassword] = useState('');
  const [isValidPassword, setIsValidPassword] = React.useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [maxApiError, setMaxApiError] = useState('');
  const [successModal, setsuccessModal] = useState(false);

  const [errors, setErrors] = useState<{
    code?: string;
    confirmPassword?: string;
  }>({});

  const { trackEvent } = useTracking();
  const navigate = useNavigate();

  const { mutate: refetchResetPassword, isPending: isLoadingReset } =
    useMutation({
      mutationKey: ['resetPassword'],
      mutationFn: () =>
        resetPassword({ email: email, password: newPassword, code: code }),

      onSuccess: () => setsuccessModal(true),
      onError: (error: any) => {
        if (error?.status == MAX_RETRY_CODE) {
          setMaxApiError(
            'You’ve reached the limit for password change attempts today. Please try again tomorrow',
          );
        }
        if (error?.data?.override?.code == ResetPasswordErrors.CODE_EXPIRED) {
          setErrors({
            code: "Sorry, we'll need you to resend the request for the code using the email address you signed up with.",
          });
        }
        if (error?.data?.override?.code == ResetPasswordErrors.CODE_INVALID) {
          setErrors({
            code: 'Use the magic code that we’ve sent to your email',
          });
        }
      },
    });

  function validateForm(): boolean {
    const newErrors: typeof errors = {};
    setMaxApiError('');
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Sorry, passwords don't match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: ChangeEvent<HTMLFormElement>) {
    e.preventDefault();
    if (validateForm()) {
      refetchResetPassword();
    }
  }

  function handleEvents(EventLabel: string) {
    trackEvent(EventNames.WellBeingDashboard, {
      eventLabel: EventLabel,
      eventAction: EventActions.click,
      eventCategory: EventLabel,
      eventSource: 'reset_password',
    });
  }

  return (
    <LoginLayout
      title="Reset your password"
      subTitle="Magic code sent! Please check your email"
      currentState={state.currentScreen}
      setState={setState}
      backButton
    >
      <form className="mt-6 grid gap-5" onSubmit={handleSubmit}>
        <Input
          label="Code"
          name="code"
          value={code}
          error={errors.code}
          onChange={(e) => setCode(validateCode(e, 4))}
        />
        <div>
          <Input
            type="password"
            label="Enter new password"
            name="new-password"
            value={newPassword}
            onChange={(e) => setnewPassword(e.target.value)}
          />

          <PasswordStrengthValidator
            onChange={(isValidPw) => setIsValidPassword(isValidPw)}
            password={newPassword}
            className="mt-3 mb-5"
          />
        </div>
        <div>
          <Input
            type="password"
            label="Re-enter new password"
            name="confirm-password"
            value={confirmPassword}
            error={errors.confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {maxApiError && (
            <p className="text-redd-600 mt-2 text-sm">{maxApiError}</p>
          )}
        </div>

        <div className="mt-8 grid gap-y-4">
          <Button
            type="submit"
            size="lg"
            disabled={
              code === '' ||
              newPassword === '' ||
              confirmPassword === '' ||
              !isValidPassword
            }
            isLoading={isLoadingReset}
            className="font-semibold"
            onClick={() => {
              handleEvents('reset_password');
            }}
          >
            Reset password
          </Button>
        </div>
      </form>

      <Modal open={successModal} modalClassName="w-[400px]">
        <div className="grid justify-normal pt-[22px] text-center">
          <img
            src={reportStatus}
            className="m-auto mb-[34px] w-[142px]"
            alt=""
          />
          <DialogTitle
            as="h4"
            className="text-black-900 mb-2 text-lg font-semibold"
          >
            Password updated!
          </DialogTitle>
          <p className="text-black-600 m-auto mb-2 max-w-[282px] text-sm">
            You may now login using your new password
          </p>

          <Button
            size="lg"
            onClick={() => {
              navigate('/login');
              handleEvents('login_after_reset');
            }}
            className="mt-9"
          >
            Login
          </Button>
        </div>
      </Modal>
    </LoginLayout>
  );
}

export default ResetPasswordScreen;
